import React, { useMemo, useState } from "react";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  FileText,
  InfoIcon,
  Plus,
  Trash,
  Pencil,
  Download,
  X,
} from "lucide-react";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";

import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useContract,
  useCreateContract,
  useDeleteContract,
  useGetCategory,
  useGetSubCategory,
  useToggleContractsStatus,
  useUpdateContract,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import { Switch } from "@shared/ui/switch";
import { useCategory, useSubCategory } from "@features/cx/consumerOnboarding/hooks";
import { FIELD_CLASSES } from "@shared/utils/constants";

export interface ContractItems {
  id: number;
  remoteUtilityId: number;
  name: string;
  termsAndConditions: string;
  file: string;
  createdDate: string;
  isActive: boolean;
  createdUserRemoteName: string | null;
  lastModifiedUserRemoteName: string | null;
  consumerCategories?: string[];
  consumerSubCategories?: string[];
}

interface ContractFormData {
  name: string;
  terms_and_conditions: string;
  file?: File;
  categories: string[];
  subCategories: string[];
}

const ContractTab: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [isAddingContract, setIsAddingContract] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractItems | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [existingFile, setExistingFile] = useState<string | null>(null);

  // Fetch contracts using the hook
  const { data: contracts = [], isLoading, refetch } = useContract();

  // Mutation hooks
  const createContractMutation = useCreateContract();
  const updateContractMutation = useUpdateContract();
  const deleteContractMutation = useDeleteContract();
  const toggleContractStatus = useToggleContractsStatus();

  const { data: categoryData } = useGetCategory();
  const [subCategoryList, setSubCategoryList] = useState<string[]>([]);
  const { data: subCategoryData } = useGetSubCategory(subCategoryList);

  const categoryOptions = useMemo(
    () =>
      categoryData
        ?.map((category) => ({
          value: category.code,
          label: category.name,
        })) || [],
    [categoryData]
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategoryData
        ?.map((subCategory) => ({
          value: subCategory.code,
          label: subCategory.name,
        })) || [],
    [subCategoryData]
  );

  const handleToggle = (contractItem: any, checked: boolean) => {
    console.log("Toggling contract status:", contractItem.id, checked);
    // Optimistically update the contract status in the UI
    toggleContractStatus.mutate({
      id: contractItem.id,
      isActive: checked,
    });
  };
  // Form fields for contract
  const contractFormFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Contract Name",
      required: true,
      placeholder: "e.g. Master Service Agreement",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "terms_and_conditions",
      type: "textarea",
      label: "Terms and Conditions",
      required: true,
      placeholder: "e.g. A contract between your company and a customer",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-semibold text-gray-700 mb-2",
        input:
          "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
        select:
          "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
        textarea:
          "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "file",
      type: "file",
      label: "Attach File",
      required: false,
      accept: ".pdf,.doc,.docx,.txt",
      fullWidth: true,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "categories",
      label: "Category",
      type: "multi-select",
      required: true,
      options: categoryOptions,
      placeholder: "Select category...",
      helperText: "Choose the account type",
      tooltipText: "Select the primary account category that best describes your account type",
      showHelperTooltip: true,
    },
    {
      name: "subCategories",
      label: "Sub-Category",
      type: "multi-select",
      required: true,
      options: subCategoryOptions,
      disabled: !subCategoryList,
      placeholder: "Select sub-category...",
      helperText: "Specify the account sub-type",
      tooltipText: "Choose a specific sub-category within your selected account category",
      showHelperTooltip: true,
    },
  ];

  // Create form instance
  const contractForm = FormService.createForm({
    fields: contractFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Handle contract form submission
  const handleContractSubmit = async (data: ContractFormData) => {
    try {
      console.log("Form data received:", data);
      console.log("Selected file state:", selectedFile);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("terms_and_conditions", data.terms_and_conditions);
      formData.append("remote_utility_id", remoteUtilityId.toString());
      data.categories.forEach((value) => {
        formData.append("consumer_categories", value);
      });
      data.subCategories.forEach((value) => {
        formData.append("consumer_sub_categories", value);
      });

      // Use file from form data first, then fall back to selectedFile
      if (data.file) {
        console.log("Using file from form data:", data.file);
        formData.append("file", data.file);
      } else if (selectedFile) {
        console.log("Using file from selectedFile state:", selectedFile);
        formData.append("file", selectedFile);
      }

      if (editingContract) {
        await updateContractMutation.mutateAsync(
          {
            id: editingContract.id,
            formData,
          },
          {
            onSuccess: () => {
              logEvent("System Settings Contract Type Modified", {
                id: editingContract.id,
                ...formData,
              });
            },
          }
        );
      } else {
        // Create contract
        await createContractMutation.mutateAsync(formData, {
          onSuccess: () => {
            logEvent("System Settings Contract Type Added", {
              ...formData,
            });
          },
        });
      }

      // Reset form and state
      contractForm.reset();
      setIsAddingContract(false);
      setEditingContract(null);
      setSelectedFile(null);
      setExistingFile(null);
      refetch();
    } catch (error) {
      console.error("Contract operation failed:", error);
    }
  };

  // Handle file selection
  const handleFileChange = (fieldName: string, value: any) => {
    if (fieldName === "file" && value instanceof File) {
      setSelectedFile(value);
      // Also set the file in the form
      contractForm.setValue("file", value);
      // Clear existing file when new file is selected
      if (existingFile) {
        setExistingFile(null);
      }
    }
    if (fieldName === "categories") {
      setSubCategoryList(value);
      if (!value) {
        // Use shouldValidate: false and shouldTouch: false to prevent triggering onChange
        contractForm.setValue("subCategories", "", {
          shouldValidate: false,
          shouldDirty: false
        });
      }
    }
  };

  // Generate file URL for download/view
  const getFileUrl = (fileName: string) => {
    // Adjust this URL pattern based on your backend file serving endpoint
    return `/api/contracts/files/${fileName}`;
  };

  // Event handlers
  const startAddContract = () => {
    contractForm.reset();
    setEditingContract(null);
    setSelectedFile(null);
    setExistingFile(null);
    setIsAddingContract(true);
  };

  const startEditContract = (contract: ContractItems) => {
    setEditingContract(contract);
    contractForm.setValue("name", contract.name);
    contractForm.setValue("terms_and_conditions", contract.termsAndConditions);
    contractForm.setValue("categories", contract.consumerCategories);
    setSubCategoryList(contract.consumerCategories);
    contractForm.setValue("subCategories", contract.consumerSubCategories);

    // Set existing file if available
    if (contract.file) {
      setExistingFile(contract.file);
    }

    // Clear any previously selected new file
    setSelectedFile(null);
    setIsAddingContract(true);
  };

  const cancelContractForm = () => {
    setIsAddingContract(false);
    setEditingContract(null);
    setSelectedFile(null);
    setExistingFile(null);
    contractForm.reset();
  };

  const deleteContract = async (contract: ContractItems) => {
    console.log("contract", contract);
    try {
      await deleteContractMutation.mutateAsync(
        { id: contract.id },
        {
          onSuccess: () => {
            logEvent("System Settings Contract Deleted", {
              contract_id: contract.id,
              contract_name: contract.name,
            });
          },
        }
      ); // 👈 actual API call
      console.log("Deleted contract:", contract.id);
      // refetch(); // 👈 refresh the data
    } catch (error) {
      console.error("Failed to delete contract:", error);
    }
  };
  const removeExistingFile = () => {
    setExistingFile(null);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    // Reset the file input in the form
    contractForm.setValue("file", null);
    // Clear the actual file input element
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  if (isLoading) {
    return (
      <div className="transition-opacity duration-500 opacity-100">
        <Card>
          <CardHeader>
            <CardTitle className="page-subheading">
              Manage Contracts
            </CardTitle>
            <CardDescription className="page-subtitle">Loading contracts...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="transition-opacity duration-500 opacity-100">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex justify-between">
            <div className="flex flex-col">
              <h3 className="card-heading-text">
                Manage Contracts
              </h3>
              <p className="card-subheading-text font-normal">
                Manage contract types and attach relevant documents. Only
                visible and editable by administrators.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={startAddContract}>
                <Plus className="w-4 h-4 mr-1" />
                Add Contract Type
              </Button>
            </div>
          </CardTitle>
          <CardDescription></CardDescription>
        </CardHeader>

        <Accordion type="single" collapsible className="mb-4">
          <AccordionItem value="help">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center">
                <InfoIcon className="h-4 w-4 mr-2" />
                Need help with Contracts?
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              <p className="mb-2">Create and manage contracts in SMART360:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Draft and store utility service contracts.</li>
                <li>Attach supporting documents for reference.</li>
                <li>Link contracts to specific consumers or services.</li>
                <li>
                  Maintain an organized record for compliance and renewals.
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <CardContent>
          {!isAddingContract ? (
            <>
              {contracts.length > 0 ? (
                <div className="space-y-4">
                  {contracts.map((contract) => (
                    <div
                      key={contract.id}
                      className="border p-4 rounded-md bg-muted/30 flex justify-between items-start"
                    >
                      <div className="flex-1">
                        <h4 className="font-medium mb-1">{contract.name}</h4>
                        <p className="text-sm text-muted-foreground mb-2">
                          {contract.termsAndConditions}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            Created:{" "}
                            {new Date(
                              contract.createdDate
                            ).toLocaleDateString()}
                          </span>
                          {contract.createdUserRemoteName && (
                            <span>By: {contract.createdUserRemoteName}</span>
                          )}
                          <span
                            className={`px-2 py-1 rounded ${contract.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                              }`}
                          >
                            {contract.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        {contract.file && (
                          <div className="mt-2">
                            <div className="font-medium text-xs mb-1">
                              Attached File
                            </div>
                            <div className="flex items-center gap-2 bg-background px-2 py-1 rounded">
                              <FileText className="w-4 h-4" />
                              <a
                                href={contract.file} // ✅ Use the raw URL
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                              >
                                {contract.file
                                  .split("/")
                                  .pop()
                                  .replace(/_/g, " ")}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Switch
                          checked={contract.isActive}
                          onCheckedChange={(checked) =>
                            handleToggle(contract, checked)
                          }
                          disabled={toggleContractStatus.isPending}
                        />

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditContract(contract)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => deleteContract(contract)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 rounded border bg-muted text-center">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="font-medium">No contracts defined yet.</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add your first contract to start tracking agreements.
                  </p>
                  <Button onClick={startAddContract}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Contract Type
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="border rounded-md p-4 mb-4">
              <h3 className="font-medium mb-4">
                {editingContract ? "Edit Contract" : "Add New Contract"}
              </h3>

              {/* Show existing file during edit */}
              {existingFile && (
                <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                  <div className="font-medium text-sm mb-2 text-blue-900">
                    Current File:
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <a
                        href={getFileUrl(existingFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        {existingFile}
                      </a>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeExistingFile}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    Upload a new file below to replace this file
                  </p>
                </div>
              )}

              <DynamicForm
                fields={contractFormFields}
                form={contractForm}
                onSubmit={handleContractSubmit}
                onReset={cancelContractForm}
                onFieldChange={handleFileChange}
                loading={
                  createContractMutation.isPending ||
                  updateContractMutation.isPending
                }
                config={{
                  showProgress: false,
                  layout: "vertical",
                  submitButtonText: editingContract ? "Update" : "Add",
                  showResetButton: true,
                  resetButtonText: "Cancel",
                  resetOnSubmit: false,
                  classes: FIELD_CLASSES,
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
              />

              {/* Show newly selected file */}
              {selectedFile && (
                <div className="mt-4 p-3 bg-green-50 rounded border border-green-200">
                  <div className="font-medium text-sm mb-2 text-green-900">
                    New File Selected:
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">
                        {selectedFile.name}
                      </span>
                      <span className="text-xs text-green-600">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeSelectedFile}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {existingFile && (
                    <p className="text-xs text-green-700 mt-1">
                      This file will replace the current file when you save
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractTab;
